import { useEffect, useRef, useState } from 'react';
import type { ToastType } from './toast-context';
import styles from './ToastBanner.module.css';

interface ToastData {
  message: string;
  type: ToastType;
  duration: number;
  persistent: boolean;
  key: number;
};

interface ToastBannerProps {
  toast: ToastData | null;
  visible: boolean;
  onDismiss: () => void;
  onExited: () => void;
};

interface Variant {
  bg: string;
  border: string;
  text: string;
  bar: string;
};

const VARIANTS: Record<ToastType, Variant> = {
  info: {
    bg: 'var(--toast-info-bg, #E6F1FB)',
    border: 'var(--toast-info-border, #378ADD)',
    text: 'var(--toast-info-text, #042C53)',
    bar: 'var(--toast-info-bar, #378ADD)',
  },
  success: {
    bg: 'var(--toast-success-bg, #EAF3DE)',
    border: 'var(--toast-success-border, #639922)',
    text: 'var(--toast-success-text, #173404)',
    bar: 'var(--toast-success-bar, #639922)',
  },
  warning: {
    bg: 'var(--toast-warning-bg, #FAEEDA)',
    border: 'var(--toast-warning-border, #BA7517)',
    text: 'var(--toast-warning-text, #412402)',
    bar: 'var(--toast-warning-bar, #EF9F27)',
  },
  danger: {
    bg: 'var(--toast-danger-bg, #FF6B6B)',
    border: 'var(--toast-danger-border, #FF6B6B)',
    text: 'var(--toast-danger-text, #FFFFFF)',
    bar: 'var(--toast-danger-bar, #E24B4A)',
  },
};

export function ToastBanner({ toast, visible, onDismiss, onExited }: ToastBannerProps) {
  const v = VARIANTS[toast?.type ?? 'info'];
  const progressRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const startRef = useRef<number>(0);
  const [, setMounted] = useState(false);

  useEffect(() => {
    if (!toast) return;
    setMounted(true);
  }, [toast?.key])

  useEffect(() => {
    if (!visible || toast?.persistent) return;

    const duration = toast!.duration;
    startRef.current = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startRef.current;
      const pct = Math.max(0, 1 - elapsed / duration);
      if (progressRef.current) progressRef.current.style.width = `${pct * 100}%`;
      if (elapsed < duration) rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [visible, toast?.key, toast?.persistent, toast?.duration])

  if (!toast) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      className={`${styles.banner} ${visible ? styles.visible : ''}`}
      style={{ backgroundColor: v.bg, borderBottom: `1px solid ${v.border}`, color: v.text, height: '60px', padding: '0' }}
      onTransitionEnd={() => {
        if (!visible) {
          setMounted(false);
          onExited();
        }
      }}
    >
      <div className={styles.inner}>
        <span className={styles.message}>{toast.message}</span>

        <button
          className={styles.close}
          onClick={onDismiss}
          aria-label="Dismiss notification"
          style={{ color: v.text }}
        >
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      {/*!toast.persistent && (
        <div
          ref={progressRef}
          className={styles.progress}
          style={{ backgroundColor: v.bar, width: '100%' }}
        />
      )*/}
    </div>
  )
}
